/**
 * Turns common KaTeX/TeX into readable plain text for clipboard paste
 * (Word, email, Slack, etc.).
 */
export function texToPlainText(tex: string): string {
  let s = tex.trim();

  // Nested \text{...}, \mathrm{...}, \operatorname{...} → inner content
  for (let i = 0; i < 8; i++) {
    const next = s.replace(
      /\\(?:text|mathrm|operatorname|mathbf|mathit|mathsf|textrm)\*{0,1}\{([^{}]*)\}/g,
      '$1'
    );
    if (next === s) break;
    s = next;
  }

  // \frac{a}{b} / \dfrac{a}{b} → a / b (parens come from \left...\right when present)
  // Brace args may contain one nesting level (TeX thousands: 500{,}000).
  for (let i = 0; i < 8; i++) {
    const next = s.replace(
      /\\d?frac\{((?:[^{}]|\{[^{}]*\})*)\}\{((?:[^{}]|\{[^{}]*\})*)\}/g,
      '$1 / $2'
    );
    if (next === s) break;
    s = next;
  }

  // TeX thousands separator: 500{,}000 → 500,000
  s = s.replace(/\{,\}/g, ',');

  const symbols: Array<[RegExp, string]> = [
    [/\\left\s*/g, ''],
    [/\\right\s*/g, ''],
    [/\\times/g, '×'],
    [/\\cdot/g, '·'],
    [/\\div/g, '÷'],
    [/\\approx/g, '≈'],
    [/\\leq|\\le\b/g, '≤'],
    [/\\geq|\\ge\b/g, '≥'],
    [/\\neq|\\ne\b/g, '≠'],
    [/\\pm/g, '±'],
    [/\\infty/g, '∞'],
    [/\\%/g, '%'],
    [/\\\$/g, '$'],
    [/\\,/g, ' '],
    [/\\;/g, ' '],
    [/\\quad/g, '  '],
    [/\\qquad/g, '    '],
    [/~/g, ' '],
    [/\\_/g, '_'],
  ];
  for (const [re, rep] of symbols) {
    s = s.replace(re, rep);
  }

  // Drop remaining simple commands: \alpha, \beta, \sqrt → keep letter-ish or strip
  s = s.replace(/\\sqrt\{([^{}]*)\}/g, '√($1)');
  s = s.replace(/\\[a-zA-Z]+\*?/g, '');

  // Braces used only for grouping
  s = s.replace(/[{}]/g, '');

  // Collapse whitespace
  s = s
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();
  s = s.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
  return s;
}

/**
 * Replaces rendered KaTeX nodes with plain-text equivalents.
 * Mutates `root` in place for clipboard plain/HTML.
 */
export function replaceKatexWithPlainText(root: ParentNode): void {
  if (!('querySelectorAll' in root)) return;

  root.querySelectorAll('.katex-mathml').forEach((mathml) => {
    const annotation = mathml.querySelector('annotation');
    const tex = (annotation?.textContent ?? '').trim();
    const plain = tex
      ? texToPlainText(tex)
      : (
          (mathml.closest('.katex')?.querySelector('.katex-html') as HTMLElement | null)
            ?.innerText ||
          mathml.textContent ||
          ''
        ).trim();

    if (!plain) return;

    const target = mathml.closest('.katex-display') ?? mathml.closest('.katex') ?? mathml;
    // Display formulas get surrounding newlines so paste reads cleanly in Word/email.
    const isDisplay = Boolean(mathml.closest('.katex-display'));
    target.replaceWith(document.createTextNode(isDisplay ? `\n${plain}\n` : plain));
  });
}

/** Plain text + HTML suitable for clipboard, with KaTeX as simple readable text. */
export function clipboardPayloadFromRendered(el: HTMLElement): {
  plain: string;
  html: string;
} {
  const clone = el.cloneNode(true) as HTMLElement;
  replaceKatexWithPlainText(clone);
  const plain = (clone.innerText || clone.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  const html = clone.innerHTML.trim();
  return { plain, html };
}
